import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell, Legend,
} from "recharts";
import { DollarSign, Users, TrendingUp, TrendingDown, Percent, Sparkles, RefreshCw, Loader2 } from "lucide-react";

const fmtUSD = (cents, opts = {}) => {
  const n = (cents || 0) / 100;
  if (opts.compact && n >= 1000) {
    if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  }
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const STATUS_STYLES = {
  active: "bg-[#34d399]/10 border-[#34d399]/40 text-[#34d399]",
  trialing: "bg-[#d4a574]/10 border-[#d4a574]/40 text-[#d4a574]",
  past_due: "bg-[#fbbf24]/10 border-[#fbbf24]/40 text-[#fbbf24]",
  canceled: "bg-[#7a6d5a]/10 border-[#7a6d5a]/40 text-[#7a6d5a]",
  incomplete: "bg-[#f87171]/10 border-[#f87171]/40 text-[#f87171]",
};

const TIER_COLORS = { broker: "#d4a574", enterprise: "#e8b988", institutional: "#7c9a85" };

export default function AdminRevenuePage() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [series, setSeries] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [k, h, r] = await Promise.all([
        api.get("/admin/revenue/kpis"),
        api.get("/admin/revenue/mrr-history?months=12"),
        api.get("/admin/revenue/recent-subscribers?limit=20"),
      ]);
      setKpis(k.data);
      setSeries(h.data.series || []);
      setSubs(r.data.subscribers || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load revenue data");
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => {
    if (user?.role !== "admin") return;
    load();
     
  }, [user]);

  if (user?.role !== "admin") return <Navigate to="/app/dashboard" />;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="animate-spin text-[#d4a574]" size={32} />
      </div>
    );
  }

  const tierBarData = kpis ? [
    { tier: "Broker", subs: kpis.count_by_tier.broker, mrr: kpis.mrr_by_tier.broker, color: TIER_COLORS.broker },
    { tier: "Enterprise", subs: kpis.count_by_tier.enterprise, mrr: kpis.mrr_by_tier.enterprise, color: TIER_COLORS.enterprise },
    { tier: "Institutional", subs: kpis.count_by_tier.institutional, mrr: kpis.mrr_by_tier.institutional, color: TIER_COLORS.institutional },
  ] : [];

  return (
    <div className="space-y-6" data-testid="admin-revenue-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Admin · Revenue</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Recurring Revenue.</h1>
          <p className="text-[13px] text-[#b8ac96] mt-2">Live from Stripe. Refreshes on demand. Share these numbers with investors, partners, or acquirers.</p>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          data-testid="revenue-refresh-btn"
          className="flex items-center gap-2 px-4 py-2 rounded border border-[#4a3a2b] text-[12px] font-semibold uppercase tracking-wider text-[#d4a574] hover:border-[#d4a574] disabled:opacity-60"
        >
          <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Headline KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Monthly Recurring Revenue" value={fmtUSD(kpis?.mrr)} accent="#34d399" testid="kpi-mrr" icon={DollarSign} sub={`ARR ${fmtUSD(kpis?.arr, { compact: true })}`} />
        <KpiCard label="Active Subscribers" value={kpis?.active_subs ?? 0} accent="#d4a574" testid="kpi-active" icon={Users} sub={`${kpis?.trialing ?? 0} in trial`} />
        <KpiCard label="Trial → Paid" value={`${kpis?.trial_to_paid_pct ?? 0}%`} accent="#e8b988" testid="kpi-conversion" icon={TrendingUp} sub={`${kpis?.trials_converted_30d ?? 0} of ${kpis?.trials_started_30d ?? 0} (30d)`} />
        <KpiCard label="Churn Rate (30d)" value={`${kpis?.churn_rate_pct ?? 0}%`} accent="#f87171" testid="kpi-churn" icon={TrendingDown} sub={`${kpis?.canceled_last_30d ?? 0} canceled`} />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniKpi label="ARPU" value={`$${kpis?.arpu_usd ?? 0}`} testid="mini-arpu" />
        <MiniKpi label="Broker MRR" value={fmtUSD(kpis?.mrr_by_tier?.broker)} color={TIER_COLORS.broker} testid="mini-broker" />
        <MiniKpi label="Enterprise MRR" value={fmtUSD(kpis?.mrr_by_tier?.enterprise)} color={TIER_COLORS.enterprise} testid="mini-enterprise" />
        <MiniKpi label="Institutional MRR" value={fmtUSD(kpis?.mrr_by_tier?.institutional)} color={TIER_COLORS.institutional} testid="mini-institutional" />
      </div>

      {/* MRR Line Chart */}
      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5" data-testid="mrr-chart-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display text-lg font-semibold flex items-center gap-2"><Sparkles size={16} className="text-[#d4a574]" /> MRR — Last 12 Months</div>
            <div className="text-[11px] text-[#7a6d5a] mt-1">Reconstructed from Stripe subscription create/cancel history.</div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={series} margin={{ top: 5, right: 20, bottom: 5, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#33291f" />
            <XAxis dataKey="label" stroke="#7a6d5a" style={{ fontSize: 11 }} />
            <YAxis stroke="#7a6d5a" style={{ fontSize: 11 }} tickFormatter={(v) => fmtUSD(v, { compact: true })} />
            <Tooltip
              contentStyle={{ background: "#241d16", border: "1px solid #4a3a2b", borderRadius: 6, fontSize: 12 }}
              formatter={(v, n) => n === "mrr" ? [fmtUSD(v), "MRR"] : [v, "Active subs"]}
              labelStyle={{ color: "#f4ede2" }}
            />
            <Line type="monotone" dataKey="mrr" stroke="#34d399" strokeWidth={2.5} dot={{ fill: "#34d399", r: 4 }} activeDot={{ r: 6 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* MRR by Tier Bar */}
      <div className="grid md:grid-cols-2 gap-5">
        <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5" data-testid="tier-chart-card">
          <div className="font-display text-lg font-semibold mb-4">Subscribers by Tier</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={tierBarData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#33291f" />
              <XAxis dataKey="tier" stroke="#7a6d5a" style={{ fontSize: 11 }} />
              <YAxis stroke="#7a6d5a" style={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#241d16", border: "1px solid #4a3a2b", borderRadius: 6, fontSize: 12 }} />
              <Bar dataKey="subs" name="Subscribers">
                {tierBarData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5" data-testid="tier-mrr-card">
          <div className="font-display text-lg font-semibold mb-4">MRR by Tier</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={tierBarData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#33291f" />
              <XAxis dataKey="tier" stroke="#7a6d5a" style={{ fontSize: 11 }} />
              <YAxis stroke="#7a6d5a" style={{ fontSize: 11 }} tickFormatter={(v) => fmtUSD(v, { compact: true })} />
              <Tooltip
                contentStyle={{ background: "#241d16", border: "1px solid #4a3a2b", borderRadius: 6, fontSize: 12 }}
                formatter={(v) => [fmtUSD(v), "MRR"]}
              />
              <Bar dataKey="mrr" name="MRR">
                {tierBarData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent subscribers table */}
      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] overflow-hidden">
        <div className="p-4 border-b border-[#33291f] flex items-center justify-between">
          <div className="font-display text-lg font-semibold">Recent Subscribers</div>
          <div className="text-[11px] text-[#7a6d5a] font-mono">Latest {subs.length}</div>
        </div>
        {subs.length === 0 ? (
          <div className="p-8 text-center text-[#7a6d5a] text-sm" data-testid="no-subs">
            No subscribers yet. Once your first customer signs up, they will appear here.
          </div>
        ) : (
          <table className="w-full text-[13px]" data-testid="subscribers-table">
            <thead className="bg-[#241d16] text-[10px] uppercase tracking-wider text-[#7a6d5a]">
              <tr>
                <th className="text-left px-4 py-2">Created</th>
                <th className="text-left px-4 py-2">Subscription</th>
                <th className="text-left px-4 py-2">Tier</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Monthly</th>
                <th className="text-left px-4 py-2">Next Bill / Trial End</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => {
                const nextTs = s.trial_end || s.current_period_end;
                const nextDate = nextTs ? new Date(nextTs * 1000).toISOString().slice(0, 10) : "—";
                return (
                  <tr key={s.id} className="border-t border-[#33291f]" data-testid={`sub-row-${s.id}`}>
                    <td className="px-4 py-2 font-mono text-[11px] text-[#b8ac96]">{s.created ? new Date(s.created * 1000).toISOString().slice(0, 10) : "—"}</td>
                    <td className="px-4 py-2 font-mono text-[11px] text-[#b8ac96]">{s.id?.slice(-14)}</td>
                    <td className="px-4 py-2"><span className="text-[11px] uppercase font-semibold" style={{ color: TIER_COLORS[s.tier] || "#b8ac96" }}>{s.tier}</span></td>
                    <td className="px-4 py-2">
                      <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${STATUS_STYLES[s.status] || "bg-[#241d16] border-[#4a3a2b] text-[#b8ac96]"}`}>{s.status}</span>
                      {s.cancel_at_period_end && <span className="ml-1 text-[9px] uppercase text-[#f87171]">Cancels</span>}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">{fmtUSD(s.monthly_amount)}</td>
                    <td className="px-4 py-2 font-mono text-[11px] text-[#b8ac96]">{nextDate}{s.status === "trialing" ? " (trial)" : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-[10px] font-mono text-[#7a6d5a]">
        Note: MRR includes active subscriptions only (trials excluded until they convert). Churn = subscriptions canceled in the last 30 days ÷ subs active 30 days ago.
      </div>
    </div>
  );
}

function KpiCard({ label, value, accent, testid, icon: Icon, sub }) {
  return (
    <div className="kpi-frame rounded-lg p-4 relative overflow-hidden" data-testid={testid}>
      <div className="flex justify-between items-start mb-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#7a6d5a] font-semibold">{label}</div>
        {Icon && <Icon size={14} style={{ color: accent }} />}
      </div>
      <div className="font-display text-3xl font-bold" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[11px] text-[#7a6d5a] mt-1 font-mono">{sub}</div>}
    </div>
  );
}

function MiniKpi({ label, value, color, testid }) {
  return (
    <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a] mb-1">{label}</div>
      <div className="font-display text-lg font-bold font-mono" style={{ color: color || "#f4ede2" }}>{value}</div>
    </div>
  );
}
